import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { Subject, takeUntil } from 'rxjs';
import { MESSAGE_OF_THE_DAY_CONFIG_TOKEN } from 'src/app/config/config.tokens';
import { MessageOfTheDayDataService } from '../../data-access/widgets/message-of-the-day-data.service';

@Component({
  selector: 'app-message-of-the-day-widget',
  template: `
    <div *ngIf="visible" class="motd-container" [class.motd-container--closing]="isClosing">
      <app-dashboard-card title="Message of the Day" [panelClass]="panelClass" [draggable]="false">
        <p *ngIf="loading">Loading message...</p>
        <p *ngIf="!loading && error" class="error">{{ error }}</p>
        <div *ngIf="!loading && !error">
          <p>{{ message || 'No message available' }}</p>
          <div class="motd-actions">
            <button mat-flat-button class="motd-ok-button" (click)="dismiss()" aria-label="Dismiss message of the day">OK</button>
          </div>
        </div>
      </app-dashboard-card>
    </div>
  `,
  styles: [
    `.motd-container {
      opacity: 1;
      max-height: 420px;
      overflow: hidden;
      transition: opacity 220ms ease, max-height 260ms ease;
      will-change: opacity, max-height;
    }`,
    `.motd-container.motd-container--closing {
      opacity: 0;
      max-height: 0;
      pointer-events: none;
    }`,
    `.motd-actions { display:flex; gap:8px; flex-wrap: wrap; }`,
    `.motd-ok-button {
      background: var(--button-main-action-bg);
      color: var(--button-main-action-text);
    }`,
    `.motd-ok-button:hover,
     .motd-ok-button:focus-visible {
      background: var(--button-main-action-bg-hover);
      color: var(--button-main-action-text-hover);
    }`
  ]
})
export class MessageOfTheDayWidgetComponent implements OnInit, OnDestroy {
  message = '';
  messageId = '';
  loading = false;
  error = '';
  visible = false;
  isClosing = false;
  panelClass = 'dashboard-card--info';
  private readonly dismissAnimationMs = 260;
  private dismissTimerId: number | null = null;
  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly dataService: MessageOfTheDayDataService,
    @Inject(MESSAGE_OF_THE_DAY_CONFIG_TOKEN) private readonly motdConfig: any
  ) { }

  ngOnInit(): void {
    this.loading = true;
    this.error = '';

    if (!this.motdConfig?.enabled) {
      this.loading = false;
      this.message = '';
      this.messageId = '';
      this.visible = false;
      this.panelClass = 'dashboard-card--info';
      return;
    }

    this.dataService.getCurrent()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (motd: any) => {
          if (motd?.enabled === false) {
            this.message = '';
            this.messageId = '';
            this.visible = false;
            this.panelClass = 'dashboard-card--info';
            return;
          }
          this.messageId = String(motd?.id || '');
          this.message = motd?.heading || motd?.description || '';
          this.panelClass = this.resolvePanelClass(motd?.status ?? motd?.type);
          this.visible = this.messageId ? this.getDismissedId() !== this.messageId : !!this.message;
        },
        error: () => {
          this.error = 'Unable to load message';
          this.message = '';
          this.messageId = '';
          this.visible = false;
          this.panelClass = 'dashboard-card--warning';
        },
        complete: () => this.loading = false
      });
  }

  dismiss(): void {
    if (!this.visible || this.isClosing) {
      return;
    }

    if (this.messageId) {
      const expires = new Date();
      expires.setDate(expires.getDate() + 365);
      document.cookie = `messageOfTheDay=${encodeURIComponent(this.messageId)};expires=${expires.toUTCString()};path=/;SameSite=Lax`;
    }

    this.isClosing = true;
    this.dismissTimerId = window.setTimeout(() => {
      this.visible = false;
      this.isClosing = false;
      this.dismissTimerId = null;
    }, this.dismissAnimationMs);
  }

  private resolvePanelClass(type: unknown): string {
    const normalized = String(type || '').trim().toLowerCase();
    if (normalized === 'danger') return 'dashboard-card--danger';
    if (normalized === 'warning') return 'dashboard-card--warning';
    if (normalized === 'success') return 'dashboard-card--success';
    return 'dashboard-card--info';
  }

  private getDismissedId(): string {
    const name = 'messageOfTheDay=';
    const parts = document.cookie.split(';').map(part => part.trim());
    const match = parts.find(part => part.startsWith(name));
    if (!match) {
      return '';
    }

    try {
      return decodeURIComponent(match.substring(name.length));
    } catch {
      return '';
    }
  }

  ngOnDestroy(): void {
    if (this.dismissTimerId !== null) {
      window.clearTimeout(this.dismissTimerId);
      this.dismissTimerId = null;
    }

    this.destroy$.next();
    this.destroy$.complete();
  }
}
